import { useMutation, useReactiveVar } from '@apollo/client/react';
import { Container, Grid, Stack } from '@mui/material';
import React, { useContext } from 'react';
import { useSearchParams } from 'react-router';

import OverwatchModal from '../../OverwatchModal';

import Banner from './Banner';
import DeliveryAndCollectionRegister from './DeliveryAndCollectionRegister';
import DiaryEntries from './DiaryEntries';
import EquipmentRegister from './EquipmentRegister';
import PeopleRegister from './PeopleRegister';
import SubcontractorRegister from './SubcontractorRegister';

import { ReportContext } from '@/Contexts/ReportContext';
import { UpdateReportDocument } from '@/graphql/generated/graphql';
import { setOpenedModals } from '@/graphql/reactiveVars';
import { type MinimizedModalsType } from '@/types/globals';
import { displayNotification } from '@/utils/notifications';

interface ViewReportModalProps {
  minimized: boolean;
  handleClose: () => void;
  handleMinimize?: (label: MinimizedModalsType['label']) => void;
}

const ViewReportModal: React.FC<ViewReportModalProps> = ({ minimized = false, handleClose }) => {
  const { selectedReport } = useContext(ReportContext);
  const openedModals = useReactiveVar(setOpenedModals);
  const [_, setSearchParams] = useSearchParams();

  const [updateReport] = useMutation(UpdateReportDocument, {
    onCompleted: () => {
      displayNotification({
        type: 'success',
        text: 'The report has been updated successfully.',
      });
    },
    onError: () => {
      displayNotification({
        type: 'error',
        text: 'An error occurred while updating the report. Please try again later.',
      });
    },
  });

  const handleApprove = () => {
    updateReport({ variables: { reportId: selectedReport?.id as string, approved: true } });
  };

  const handleReject = () => {
    updateReport({ variables: { reportId: selectedReport?.id as string, approved: false } });
  };

  return (
    <OverwatchModal
      size="xl"
      handleClose={() => {
        handleClose();
        setSearchParams({});
      }}
      title="View Report"
      caption="View the details of the report"
      primaryButtonText={selectedReport?.approved ? 'Approved' : 'Approve'}
      primaryButtonAction={handleApprove}
      secondaryButtonText={selectedReport?.approved ? 'Revert' : 'Reject'}
      secondaryButtonAction={handleReject}
      tertiaryButtonText="Edit"
      tertiaryButtonAction={() => {
        setOpenedModals([...openedModals, 'EditReport']);
      }}
      disablePrimary={selectedReport?.approved}
      disableTertiary={selectedReport?.approved}
      minimized={minimized}
    >
      <Container maxWidth="xl">
        <Banner />
        <DiaryEntries />

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Stack spacing={3}>
              <PeopleRegister />
              <EquipmentRegister />
              <SubcontractorRegister />
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <DeliveryAndCollectionRegister />
          </Grid>
        </Grid>
      </Container>
    </OverwatchModal>
  );
};

export default ViewReportModal;
